import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faPipe,
  faPlus,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { DotIndicator } from '../dot-indicator/dot-indicator';

const arrowPositionCva = cva(
  [
    'absolute',
    'z-10',
    'flex',
    'items-center',
    'justify-center',
    'h-10',
    'w-10',
    'rounded-full',
    'bg-white/80',
    'hover:bg-white',
    'shadow-md',
    'transition-opacity',
  ],
  {
    variants: {
      position: {
        top: 'top-0 translate-y-0',
        center: 'top-1/2 -translate-y-1/2',
        bottom: 'bottom-0 translate-y-0',
      },
      side: {
        left: 'left-0',
        right: 'right-0',
      },
      disabled: {
        true: 'opacity-50 cursor-not-allowed',
        false: '',
      },
    },
    defaultVariants: {
      position: 'center',
      disabled: false,
    },
  }
);

export interface SlideShowProps {
  contents: ReactNode[];
  arrowPosition: 'top' | 'center' | 'bottom';
  onLastSlideRightArrowClick?: () => void;
  current: number;
  setCurrent: (index: (prev: number) => number) => void;
  onDeleteSlide?: (index: number) => void;
}

export const SlideShow = ({
  contents,
  arrowPosition = 'center',
  onLastSlideRightArrowClick,
  current,
  setCurrent,
  onDeleteSlide,
}: SlideShowProps) => {
  const prevSlide = () =>
    setCurrent((prev) => (prev === 0 ? contents.length - 1 : prev - 1));
  const nextSlide = () =>
    setCurrent((prev) => (prev === contents.length - 1 ? 0 : prev + 1));

  const isDeleteDisabled = contents.length === 1;
  const isPrevDisabled = current === 0;
  const isNextDisabled = false; // Only disable if you want to restrict at last slide

  return (
    <div className="slide-show relative flex flex-col items-center w-full min-h-0">
      {/* Slide number label and trashcan */}
      <div className="flex items-center justify-between px-6 pt-4 pb-2">
        <span className="text-2xl font-bold">
          {current + 1} of {contents.length}
        </span>
        <button
          className={`ml-4 text-red-600 hover:text-red-800 transition-colors ${
            isDeleteDisabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          aria-label="Delete Slide"
          type="button"
          onClick={() => onDeleteSlide?.(current)}
          disabled={isDeleteDisabled}
        >
          <FontAwesomeIcon icon={faTrash} size="lg" />
        </button>
      </div>
      <div className="py-4">
        <DotIndicator
          numDots={contents.length}
          selectedDot={current}
          setSelectedDot={(index) => setCurrent((prevNumber) => index)}
        />
      </div>

      {/* Left Arrow */}
      <button
        className={arrowPositionCva({
          position: arrowPosition,
          side: 'left',
          disabled: isPrevDisabled,
        })}
        onClick={isPrevDisabled ? undefined : prevSlide}
        aria-label="Previous Slide"
        type="button"
        disabled={isPrevDisabled}
      >
        <span className="text-2xl">
          {isPrevDisabled ? (
            <FontAwesomeIcon icon={faPipe} />
          ) : (
            <FontAwesomeIcon icon={faChevronLeft} />
          )}
        </span>
      </button>

      {/* Slide Viewer */}
      <div className="slide-viewer w-full h-full overflow-hidden flex items-center justify-center">
        <div
          className="slide w-full h-full transition-transform duration-500"
          style={{
            transform: `translateX(-${current * 100}%)`,
            display: 'flex',
          }}
        >
          {contents.map((content, idx) => (
            <div
              key={idx}
              className="w-full h-full flex-shrink-0 flex items-center justify-center"
            >
              {content}
            </div>
          ))}
        </div>
      </div>

      {/* Right Arrow */}
      <button
        className={arrowPositionCva({
          position: arrowPosition,
          side: 'right',
          disabled: isNextDisabled,
        })}
        onClick={
          current === contents.length - 1
            ? onLastSlideRightArrowClick
              ? onLastSlideRightArrowClick
              : undefined
            : nextSlide
        }
        aria-label="Next Slide"
        type="button"
        disabled={isNextDisabled}
      >
        <span className="text-2xl">
          {current === contents.length - 1 ? (
            <FontAwesomeIcon icon={faPlus} />
          ) : (
            <FontAwesomeIcon icon={faChevronRight} />
          )}
        </span>
      </button>
    </div>
  );
};
